<template>
  <cs-app>
    <template v-slot:content>
      <div v-if="clientCase.closeReason" v-html="clientCase.closeReason">
        {{ clientCase.closeReason }}
      </div>
      <div v-else>
        <div class="lawyer-details" v-if="bidAccepted">
          <lawyer-details :lawyer="lawyer" />
        </div>


        <div v-else-if="!bidAccepted" class="pending-bids">
          <p>
            Below, you can view the details of any bid(s) that have been applied to your case and also a summary of your posted case information.
          </p>
        <span class="md-title">Lawyer Bids(s)</span>
          <p v-if="bids.length === 0">
            Lawyers are reviewing your case right now! If/when any of them decide to place a bid, the details of each
            bid will replace this text and be posted here.
          </p>
          <md-table
            v-if="bids.length > 0"
            class="lawyer-bids-table"
            v-model="bids"
            @md-selected="viewBidDetails"
          >
            <md-table-row>
              <md-table-head>Age</md-table-head>
              <md-table-head>Sex</md-table-head>
              <md-table-head>Years of Exp.</md-table-head>
              <md-table-head>Areas of Expertise</md-table-head>
              <md-table-head>Spoken Languages</md-table-head>
              <md-table-head>Firm Size</md-table-head>
              <md-table-head>Location</md-table-head>
              <md-table-head>Cost Details</md-table-head>
            </md-table-row>

            <md-table-row
              slot="md-table-row"
              slot-scope="{ item }"
              md-selectable="single"
            >
              <md-table-cell md-label="Age">{{ item.lawyer.profile.ageGroup }}</md-table-cell>
              <md-table-cell md-label="Sex">{{ item.lawyer.profile.sex }}</md-table-cell>
              <md-table-cell md-label="Years of Exp.">{{ item.lawyer.profile.yearsOfExperience }}</md-table-cell>
              <md-table-cell>
                <ul>
                  <li v-for="expertise in item.lawyer.profile.areasOfExpertise">
                    {{ expertise }}
                  </li>
                </ul>
              </md-table-cell>
              <md-table-cell md-label="Spoken Languages">
                <ul>
                  <li v-for="language in item.lawyer.profile.spokenLanguages">
                    {{ language }}
                  </li>
                </ul>
              </md-table-cell>
              <md-table-cell md-label="Firm Size">{{ item.lawyer.profile.firmSize }}</md-table-cell>
              <md-table-cell md-label="Location">{{ item.lawyer.profile.firmLocation }}</md-table-cell>
              <md-table-cell md-label="Cost Details">
                <ul>
                  <li v-for="costDetail in item.costDetails">
                    {{ costDetail.label }}: {{ costDetail.amount }}{{ costDetail.rate }}
                  </li>
                </ul>
              </md-table-cell>
            </md-table-row>
          </md-table>
        </div>

        <span class="md-title">Case Summary</span>
        <md-table>
          <md-table-row>
            <md-table-head>Question</md-table-head>
            <md-table-head>Answer</md-table-head>
          </md-table-row>

          <md-table-row v-for="field in fields" :key=field.id>
            <md-table-cell>{{ field.title }}</md-table-cell>
            <md-table-cell>
              <response-answer v-if="field.answer" :answer="field.answer" />
            </md-table-cell>
          </md-table-row>
        </md-table>

        <!-- Bid Details -->
        <md-dialog :md-active.sync="showDialog" v-if="showDialog">
          <md-dialog-title>Bid Details</md-dialog-title>
          <md-dialog-content>
            <md-table class="lawyer-bids-table">
              <md-table-row>
                <md-table-head>Age</md-table-head>
                <md-table-head>Sex</md-table-head>
                <md-table-head>Years of Exp.</md-table-head>
                <md-table-head>Spoken Languages</md-table-head>
                <md-table-head>Firm Size</md-table-head>
                <md-table-head>Location</md-table-head>
              </md-table-row>

              <md-table-row>
                <md-table-cell>{{ selectedBid.age }}</md-table-cell>
                <md-table-cell>{{ selectedBid.sex }}</md-table-cell>
                <md-table-cell>{{ selectedBid.yearsOfExp }}</md-table-cell>
                <md-table-cell>
                  <ul>
                    <li v-for="language in selectedBid.spokenLanguages">
                      {{ language }}
                    </li>
                  </ul>
                </md-table-cell>
                <md-table-cell>{{ selectedBid.firmSize }}</md-table-cell>
                <md-table-cell>{{ selectedBid.location }}</md-table-cell>
              </md-table-row>
            </md-table>

            <!-- Areas of Expertise -->
            <span class="md-sub-heading">Claimed Areas of Expertise</span>
            <ul>
              <li v-for="expertise in selectedBid.areasOfExpertise">
                {{ expertise }}
              </li>
            </ul>

            <!-- Cost Details -->
            <span class="md-sub-heading">Cost Details</span>
            <ul>
              <li v-for="costDetail in selectedBid.costDetails">
                {{ costDetail.label }}: {{ costDetail.amount }}{{ costDetail.rate }}
              </li>
            </ul>
          </md-dialog-content>
          <md-dialog-actions>
            <md-button class="md-default" @click="showDialog = false">Close</md-button>
            <md-button class="md-primary" @click="showDialog = false">Accept</md-button>
          </md-dialog-actions>
      </md-dialog>
    </div>
    </template>
  </cs-app>
</template>

<style>
.lawyer-bids-table ul {
  list-style-type: none;
  margin: 0;
  padding: 0;
}
</style>

<script>
import { mapGetters } from 'vuex'
import CSApp from "@/components/CSApp"
import { clientBidService } from '@/services/clientBidService'
import ResponseAnswer from '@/components/ResponseAnswer'
import LawyerDetails from '@/components/LawyerDetails'
import { bidTransformer } from '@/data/bidTransformer'

export default {
  name: "ClientCasePage",
  components: {
    'cs-app': CSApp,
    ResponseAnswer,
    LawyerDetails,
  },
  data () {
    return {
      showDialog: false,
      selectedBid: null,
      bids: [],
      fields: [],
      lawyer: null,
    }
  },
  computed: {
    bidAccepted () {
      return this.bids.filter(bid => bid.acceptedAt).length > 0
    },
    ...mapGetters([
      'clientCase'
    ])
  },
  created () {
    this.$http.get(`${process.env.VUE_APP_API_URL}/cases/${this.$route.params.id}`).then((response) => {
      this.$store.commit('addClientCase', response.data);
      this.fields = this.clientCase.form.fields.map(field => {
        return { id: field.id, title: field.title }
      }).map(field => {
        let answer = this.clientCase.response.answers.find(answer => answer.field.id === field.id)
        return { ...field, answer }
      })

      return this.clientCase.id;
    }).then(caseId => {
        return clientBidService(this.$http).for(this.clientCase.id).all().then(response => {
          this.bids = response.data.map(bid => bidTransformer(bid))
        })
    }).then(() => {
      let lawyerId = this.bids.filter(bid => bid.acceptedAt)[0].lawyerId
      this.$http.get(`${process.env.VUE_APP_API_URL}/lawyers/${lawyerId}`).then(response => {
        this.lawyer = response.data
      })
    })

  },
  methods: {
    selectBid (bid) {
      this.showDialog = true
      this.selectedBid = bid
    },
    viewBidDetails(bid) {
      this.$store.commit('addSelectedClientBid', bid)
      this.$router.push({
        name: "cases.bids.show",
        params: {
            caseId: this.$store.getters.clientCase.id,
            bidId: bid.id
          }
      });
    }
  }
};
</script>
